
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";

export interface TopicPerformance {
  topic: string;
  accuracy: number;
  averageTime: number;
  questionsAttempted: number;
  incorrectQuestions: {
    questionId: string;
    question: string;
    selectedAnswer: string;
    timeTaken: number;
  }[];
}

interface PerformanceMetrics {
  averageTimePerQuestion: number;
  weakTopics: string[];
  strongTopics: string[];
  accuracy: number;
  topicPerformance: TopicPerformance[];
  documentSectionCorrelation: {
    sectionId: string;
    accuracy: number;
    timeTaken: number;
  }[];
}

export function useCurrentAffairsPerformance() {
  const { userId } = useAuthSession();

  return useQuery({
    queryKey: ['current-affairs-performance', userId],
    queryFn: async (): Promise<PerformanceMetrics> => {
      if (!userId) {
        throw new Error('User must be logged in');
      }

      const { data: performanceData, error } = await supabase
        .from('current_affairs_performance_details')
        .select(`
          *,
          current_affairs_questions(
            question,
            date,
            time_period,
            metadata
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching performance data:', error);
        throw error;
      }

      // Initialize metrics
      const totalQuestions = performanceData.length;
      if (totalQuestions === 0) {
        return {
          averageTimePerQuestion: 0,
          weakTopics: [],
          strongTopics: [],
          accuracy: 0,
          topicPerformance: [],
          documentSectionCorrelation: []
        };
      }

      // Track topic-wise performance
      const topicStats = new Map<string, {
        correct: number;
        total: number;
        totalTime: number;
        incorrectQuestions: any[];
      }>();

      // Track document section correlation
      const sectionStats = new Map<string, {
        correct: number;
        total: number;
        totalTime: number;
      }>();

      // Analyze each question attempt
      performanceData.forEach(record => {
        // Topic analysis
        const topic = record.topic;
        const current = topicStats.get(topic) || {
          correct: 0,
          total: 0,
          totalTime: 0,
          incorrectQuestions: []
        };

        current.total++;
        current.totalTime += record.time_taken || 0;
        
        if (record.correct) {
          current.correct++;
        } else {
          current.incorrectQuestions.push({
            questionId: record.question_id,
            question: record.current_affairs_questions?.question,
            selectedAnswer: record.answer_selected,
            timeTaken: record.time_taken
          });
        }
        
        topicStats.set(topic, current);

        // Document section analysis
        const sectionId = record.current_affairs_questions?.metadata?.section_id;
        if (sectionId) {
          const sectionStat = sectionStats.get(sectionId) || {
            correct: 0,
            total: 0,
            totalTime: 0
          };
          
          sectionStat.total++;
          sectionStat.totalTime += record.time_taken || 0;
          if (record.correct) sectionStat.correct++;
          
          sectionStats.set(sectionId, sectionStat);
        }
      });

      // Calculate topic performance metrics
      const topicPerformance: TopicPerformance[] = Array.from(topicStats.entries())
        .map(([topic, stats]) => ({
          topic,
          accuracy: (stats.correct / stats.total) * 100,
          averageTime: stats.totalTime / stats.total,
          questionsAttempted: stats.total,
          incorrectQuestions: stats.incorrectQuestions
        }));

      // Identify weak and strong topics
      const weakTopics = topicPerformance
        .filter(topic => topic.accuracy < 70)
        .map(topic => topic.topic);

      const strongTopics = topicPerformance
        .filter(topic => topic.accuracy >= 70)
        .map(topic => topic.topic);

      // Calculate document section correlation
      const documentSectionCorrelation = Array.from(sectionStats.entries())
        .map(([sectionId, stats]) => ({
          sectionId,
          accuracy: (stats.correct / stats.total) * 100,
          timeTaken: stats.totalTime / stats.total
        }));

      // Calculate overall metrics
      const totalTime = performanceData.reduce((sum, record) => sum + (record.time_taken || 0), 0);
      const correctAnswers = performanceData.filter(record => record.correct).length;

      return {
        averageTimePerQuestion: totalTime / totalQuestions,
        weakTopics,
        strongTopics,
        accuracy: (correctAnswers / totalQuestions) * 100,
        topicPerformance,
        documentSectionCorrelation
      };
    },
    enabled: !!userId
  });
}

